/**
 * JSON-LD Structured Data for LLMO (Large Language Model Optimization)
 * These schemas help AI crawlers and search engines understand the site content
 */

const SITE_URL = "https://nishikigoi.fr";
const SITE_NAME = "Nishikigoi.fr";
const SITE_LOGO = "https://nishikigoi.fr/logo.png";

/**
 * Organization schema - represents the website/business
 */
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": SITE_NAME,
  "alternateName": "Nishikigoi France",
  "url": SITE_URL,
  "logo": SITE_LOGO,
  "description": "La reference francaise des carpes koi japonaises (Nishikigoi). Guide complet des varietes, eleveurs, soins et alimentation.",
  "sameAs": [
    "https://www.youtube.com/@afloredeau",
    "https://afloredeau.com"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "availableLanguage": "French"
  }
};

/**
 * WebSite schema - main site information
 */
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": SITE_NAME,
  "alternateName": "Nishikigoi France - Carpes Koi",
  "url": SITE_URL,
  "description": "Tout savoir sur les carpes koi et nishikigoi : 100+ varietes, eleveurs japonais de Niigata, soins, alimentation, concours. La reference francaise du koi japonais.",
  "inLanguage": "fr-FR",
  "publisher": organizationSchema,
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${SITE_URL}/varietes?search={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
};

/**
 * Breadcrumb generator
 */
export const generateBreadcrumb = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url ? `${SITE_URL}${item.url}` : undefined
  }))
});

/**
 * Article schema for blog posts and informational pages
 */
export const generateArticleSchema = ({ title, description, url, image, datePublished, dateModified, author = "Nishikigoi.fr" }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": title,
  "description": description,
  "image": image || `${SITE_URL}/og-image.jpg`,
  "url": `${SITE_URL}${url}`,
  "datePublished": datePublished || new Date().toISOString(),
  "dateModified": dateModified || new Date().toISOString(),
  "author": {
    "@type": "Organization",
    "name": author,
    "url": SITE_URL
  },
  "publisher": organizationSchema,
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `${SITE_URL}${url}`
  },
  "inLanguage": "fr-FR"
});

/**
 * FAQ schema for FAQ pages
 */
export const generateFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

/**
 * Product schema for koi varieties
 */
export const generateVarietySchema = (variety) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  "name": variety.name,
  "description": variety.description,
  "image": variety.image_url,
  "url": `${SITE_URL}/varietes/${variety.slug || variety.id}`,
  "category": "Nishikigoi - Carpe Koi",
  "brand": {
    "@type": "Brand",
    "name": variety.family || "Nishikigoi"
  },
  "additionalProperty": [
    {
      "@type": "PropertyValue",
      "name": "Rarete",
      "value": variety.rarity
    },
    {
      "@type": "PropertyValue",
      "name": "Famille",
      "value": variety.family
    }
  ]
});

/**
 * ItemList schema for collection pages
 */
export const generateItemListSchema = (title, description, items) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": title,
  "description": description,
  "numberOfItems": items.length,
  "itemListElement": items.slice(0, 10).map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "url": item.url ? `${SITE_URL}${item.url}` : undefined
  }))
});

/**
 * LocalBusiness schema for breeder pages
 */
export const generateBreederSchema = (breeder) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": breeder.name,
  "description": breeder.description,
  "image": breeder.image_url,
  "url": `${SITE_URL}/eleveurs/${breeder.slug || breeder.id}`,
  "address": {
    "@type": "PostalAddress",
    "addressLocality": breeder.location || "Niigata",
    "addressCountry": "JP"
  },
  "priceRange": breeder.price_range || "$$$$",
  "knowsAbout": breeder.specialties || ["Nishikigoi", "Carpe Koi"]
});

/**
 * HowTo schema for care/tutorial pages
 */
export const generateHowToSchema = ({ title, description, steps }) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": title,
  "description": description,
  "step": steps.map((step, index) => ({
    "@type": "HowToStep",
    "position": index + 1,
    "name": step.name,
    "text": step.text
  }))
});

/**
 * CollectionPage schema for the homepage
 */
export const homePageSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": "Nishikigoi.fr - La Reference des Carpes Koi Japonaises",
  "description": "Tout savoir sur les carpes koi et nishikigoi : 100+ varietes, eleveurs japonais de Niigata, soins, alimentation, prix. La reference francaise du koi japonais.",
  "url": SITE_URL,
  "inLanguage": "fr-FR",
  "isPartOf": websiteSchema,
  "about": {
    "@type": "Thing",
    "name": "Carpes Koi",
    "alternateName": ["Nishikigoi", "Koi Fish", "Carpe Japonaise"],
    "description": "Poisson d'ornement originaire du Japon, eleve pour ses couleurs et motifs exceptionnels"
  },
  "mainEntity": {
    "@type": "ItemList",
    "name": "Varietes de Nishikigoi",
    "description": "Collection complete de plus de 100 varietes de carpes koi japonaises",
    "numberOfItems": "100+"
  }
};

/**
 * Combined schemas for homepage (multiple schemas in array)
 */
export const getHomePageSchemas = (popularVarieties = []) => [
  websiteSchema,
  organizationSchema,
  {
    ...homePageSchema,
    mainEntity: {
      "@type": "ItemList",
      "name": "Varietes Populaires de Nishikigoi",
      "numberOfItems": popularVarieties.length || "100+",
      "itemListElement": popularVarieties.slice(0, 6).map((v, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": v.name,
        "url": `${SITE_URL}/varietes/${v.slug || v.id}`
      }))
    }
  },
  generateBreadcrumb([{ name: "Accueil" }])
];

/**
 * Varieties page schema
 */
export const getVarietiesPageSchemas = (varieties = []) => [
  generateBreadcrumb([
    { name: "Accueil", url: "/" },
    { name: "Varietes de Koi" }
  ]),
  generateItemListSchema(
    "Encyclopedie des Varietes de Carpes Koi",
    "Collection complete de plus de 100 varietes de nishikigoi japonais avec photos, descriptions et caracteristiques",
    varieties.map(v => ({ name: v.name, url: `/varietes/${v.slug || v.id}` }))
  )
];

/**
 * History page schema
 */
export const getHistoryPageSchemas = () => [
  generateBreadcrumb([
    { name: "Accueil", url: "/" },
    { name: "Histoire des Carpes Koi" }
  ]),
  generateArticleSchema({
    title: "Histoire des Carpes Koi - Origines et Culture du Nishikigoi",
    description: "L'histoire fascinante des carpes koi : de la Chine ancienne au Japon, comment le nishikigoi est devenu le poisson le plus precieux au monde.",
    url: "/histoire",
    datePublished: "2024-01-01",
    dateModified: new Date().toISOString()
  })
];

/**
 * Care page schema
 */
export const getCarePageSchemas = () => [
  generateBreadcrumb([
    { name: "Accueil", url: "/" },
    { name: "Soins des Koi" }
  ]),
  generateHowToSchema({
    title: "Guide Complet des Soins pour Carpes Koi",
    description: "Apprenez a prendre soin de vos carpes koi : maladies, traitements, qualite de l'eau et entretien du bassin",
    steps: [
      { name: "Surveiller la qualite de l'eau", text: "Testez regulierement le pH, l'ammoniac et les nitrites de votre bassin" },
      { name: "Observer vos koi quotidiennement", text: "Detectez rapidement les signes de maladie : comportement anormal, lesions, parasites" },
      { name: "Maintenir une bonne filtration", text: "Un systeme de filtration adapte est essentiel pour la sante des koi" },
      { name: "Adapter l'alimentation", text: "Ajustez la nourriture selon la saison et la temperature de l'eau" }
    ]
  })
];

/**
 * Food page schema
 */
export const getFoodPageSchemas = () => [
  generateBreadcrumb([
    { name: "Accueil", url: "/" },
    { name: "Alimentation des Koi" }
  ]),
  generateHowToSchema({
    title: "Guide d'Alimentation des Carpes Koi",
    description: "Quelle nourriture pour vos carpes koi ? Guide complet selon la saison, la temperature et les besoins nutritionnels",
    steps: [
      { name: "Choisir la bonne nourriture", text: "Selectionnez des granules de qualite adaptees a la taille de vos koi" },
      { name: "Respecter les quantites", text: "Donnez ce que vos koi peuvent manger en 5 minutes maximum" },
      { name: "Adapter selon la saison", text: "Reduisez l'alimentation en hiver, augmentez en ete" },
      { name: "Varier l'alimentation", text: "Alternez entre nourriture de croissance, de couleur et friandises" }
    ]
  })
];

/**
 * Breeders page schema
 */
export const getBreedersPageSchemas = (breeders = []) => [
  generateBreadcrumb([
    { name: "Accueil", url: "/" },
    { name: "Eleveurs de Koi" }
  ]),
  generateItemListSchema(
    "Les Meilleurs Eleveurs de Koi Japonais",
    "Decouvrez les eleveurs de carpes koi les plus renommes du Japon, de Niigata a Hiroshima",
    breeders.map(b => ({ name: b.name, url: `/eleveurs/${b.slug || b.id}` }))
  )
];

/**
 * Blog page schema
 */
export const getBlogPageSchemas = (posts = []) => [
  generateBreadcrumb([
    { name: "Accueil", url: "/" },
    { name: "Blog Koi" }
  ]),
  {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Blog Nishikigoi.fr",
    "description": "Articles et conseils d'experts sur les carpes koi : soins, alimentation, varietes, bassins",
    "url": `${SITE_URL}/blog`,
    "inLanguage": "fr-FR",
    "blogPost": posts.slice(0, 10).map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "url": `${SITE_URL}/blog/${post.slug}`,
      "datePublished": post.created_at || new Date().toISOString()
    }))
  }
];

/**
 * Contest page schema
 */
export const getContestPageSchemas = () => [
  generateBreadcrumb([
    { name: "Accueil", url: "/" },
    { name: "Concours de Koi" }
  ]),
  {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Concours de Carpes Koi",
    "description": "Calendrier des concours de carpes koi en France et au Japon. Champions, criteres de jugement et inscriptions.",
    "url": `${SITE_URL}/concours`,
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "location": {
      "@type": "Place",
      "name": "France et Japon"
    }
  }
];

/**
 * Lexicon page schema
 */
export const getLexiconPageSchemas = (terms = []) => [
  generateBreadcrumb([
    { name: "Accueil", url: "/" },
    { name: "Lexique Koi" }
  ]),
  {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "name": "Lexique des Termes Japonais du Koi",
    "description": "Dictionnaire complet des termes japonais utilises dans le monde du nishikigoi",
    "url": `${SITE_URL}/lexique`,
    "inLanguage": ["fr-FR", "ja"],
    "hasDefinedTerm": terms.slice(0, 20).map(term => ({
      "@type": "DefinedTerm",
      "name": term.term,
      "description": term.definition
    }))
  }
];

export default {
  organizationSchema,
  websiteSchema,
  homePageSchema,
  generateBreadcrumb,
  generateArticleSchema,
  generateFAQSchema,
  generateVarietySchema,
  generateItemListSchema,
  generateBreederSchema,
  generateHowToSchema,
  getHomePageSchemas,
  getVarietiesPageSchemas,
  getHistoryPageSchemas,
  getCarePageSchemas,
  getFoodPageSchemas,
  getBreedersPageSchemas,
  getBlogPageSchemas,
  getContestPageSchemas,
  getLexiconPageSchemas
};
